import { ApiRequestError } from "@/api/client";

/**
 * Structured info from the server's field-completeness gate (Patent G domain
 * "field_completeness"). A create/update the gate refuses — e.g. an orphan
 * impl_task with no component/story parent (FB-0137) — 409s with a body of
 * shape { error_code, field, recovery_guidance, g_domain: "field_completeness" }.
 */
export interface FieldCompletenessGate {
  field?: string;
  guidance?: string;
}

/**
 * If `err` is a field-completeness-gate 409, return its actionable info so the
 * caller can show the server's guidance ("this task needs a component") instead
 * of a generic "log this error?" ticket. Returns null for anything else.
 *
 * `ApiRequestError.data` preserves the server's structured `detail` (client.ts).
 */
export function fieldCompletenessGateInfo(
  err: unknown,
): FieldCompletenessGate | null {
  if (
    err instanceof ApiRequestError &&
    err.status === 409 &&
    err.data &&
    typeof err.data === "object"
  ) {
    const d = err.data as {
      g_domain?: string;
      field?: string;
      recovery_guidance?: string;
    };
    if (d.g_domain === "field_completeness") {
      return { field: d.field, guidance: d.recovery_guidance };
    }
  }
  return null;
}

/**
 * Structured info from the plan-limit refusal. Exact server shape, from
 * app/api/v1/projects.py:546-556 — status 402 with
 * `{error: "project_limit_reached", message, current, limit}`.
 */
export interface PlanLimitRefusal {
  message: string;
  current?: number;
  limit?: number;
}

/**
 * If `err` is a plan-limit 402, return its info so the caller can explain the
 * billing situation and point at Billing.
 *
 * This exists because a 402 was falling through to "shall I log this as a bug?"
 * — Felix offering to file a DEFECT TICKET against a correct billing decision.
 * That is noise in the feedback queue and it tells the customer the product is
 * broken when the product is working exactly as sold.
 *
 * Deliberately keyed on BOTH the status and the `error` code rather than on the
 * message text: the sentence is user-facing prose that will be reworded, and a
 * detector that greps prose silently stops matching when someone improves the
 * copy — the exact "a check that cannot fail" shape this codebase keeps hitting.
 */
export function planLimitInfo(err: unknown): PlanLimitRefusal | null {
  if (
    err instanceof ApiRequestError &&
    err.status === 402 &&
    err.data &&
    typeof err.data === "object"
  ) {
    const d = err.data as {
      error?: string;
      message?: string;
      current?: number;
      limit?: number;
    };
    if (d.error === "project_limit_reached") {
      return {
        // The server already phrases this well ("Your plan includes 25 projects
        // and you're using 30. Upgrade your plan to create more."). Fall back to
        // err.detail only if `message` is absent, so this never renders empty.
        message: d.message || err.detail,
        current: d.current,
        limit: d.limit,
      };
    }
  }
  return null;
}
